export type ResolvedAttachment = {
  kind: 'resolved';
  name: string;
  title: string;
  uri?: string;
};

// Pending attachments must never appear in a sent message — they exist only
// to round-trip Drive picks until a chat exists to mint an ExternalDocument.
export type PendingAttachment = {
  kind: 'pending';
  sourceType: 'GOOGLE_DOCS' | 'GOOGLE_SLIDES' | 'NOTION';
  sourceId: string;
  title: string;
  uri?: string;
};

export type UploadingImageAttachment = {
  kind: 'uploading-image';
  uploadId: string;
  fileName: string;
  localPreviewUrl: string;
  sizeBytes: number;
};

export type ImageAttachment = {
  kind: 'image';
  imageUri: string;
  fileName: string;
  sizeBytes: number;
};

export type DocAttachment = ResolvedAttachment | PendingAttachment;

export type ChatAttachment =
  | ResolvedAttachment
  | PendingAttachment
  | UploadingImageAttachment
  | ImageAttachment;

export const splitAttachments = (
  attachments: ChatAttachment[],
): { imageUris: string[]; docAttachments: DocAttachment[] } => ({
  imageUris: attachments
    .filter((a): a is ImageAttachment => a.kind === 'image')
    .map(a => a.imageUri),
  docAttachments: attachments.filter(
    (a): a is DocAttachment => a.kind === 'resolved' || a.kind === 'pending',
  ),
});

export const attachmentKey = (attachment: ChatAttachment): string => {
  switch (attachment.kind) {
    case 'resolved':
      return attachment.name;
    case 'pending':
      return `pending:${attachment.sourceId}`;
    case 'uploading-image':
      return `uploading:${attachment.uploadId}`;
    case 'image':
      return `image:${attachment.imageUri}`;
    default: {
      const exhaustive: never = attachment;
      return String(exhaustive);
    }
  }
};
